import type { Tone } from '../../components/StatusBadge';
import type { AuthedRequest } from '../auth/AuthProvider';

/** Active, suspended or cancelled (clients and accounts, ADR-026). */
export type Situation = 'active' | 'suspended' | 'cancelled';

export const situationLabels: Record<Situation, string> = {
  active: 'Ativo',
  suspended: 'Suspenso',
  cancelled: 'Cancelado',
};

export const situationTone: Record<Situation, Tone> = {
  active: 'ok',
  suspended: 'warn',
  cancelled: 'neutral',
};

/** A client of the monitoring company (ADR-026). */
export interface Client {
  id: string;
  name: string;
  /** CPF or CNPJ, digits only. */
  document: string | null;
  status: Situation;
  notes: string;
  account_count: number;
  device_count: number;
  created_at: string;
  updated_at: string;
}

export interface ClientBody {
  name?: string;
  document?: string;
  status?: Situation;
  notes?: string;
}

/**
 * Mirrors the public contract of /api/v1/sites and /api/v1/devices. A site is
 * an account ("conta") of a client: a monitored place.
 */
export interface Site {
  id: string;
  client: { id: string; name: string };
  status: Situation;
  name: string;
  address: string;
  /** Null means the site uses the organisation's time zone. */
  timezone: string | null;
  device_count: number;
  created_at: string;
}

export type DeviceKind = 'camera' | 'nvr' | 'dvr' | 'encoder';
export type DeviceStatus = 'unknown' | 'online' | 'offline' | 'error';
/**
 * 'rtsp' uses only the RTSP port with configured paths; 'onvif' discovers
 * over HTTP; 'rtmp' is the reverse connection: the device pushes to the server.
 */
export type Connection = 'onvif' | 'rtsp' | 'rtmp';

export interface Device {
  id: string;
  site: { id: string; name: string };
  /** The client of the device's account (ADR-026). */
  client?: { id: string; name: string };
  name: string;
  kind: DeviceKind;
  vendor: string;
  model: string;
  host: string;
  http_port: number;
  rtsp_port: number;
  connection: Connection;
  /** Path and query of the RTSP streams (connection 'rtsp'); sub is optional. */
  rtsp_main_path: string;
  rtsp_sub_path: string;
  username: string;
  /** An RTMP push key exists (the key is shown only when generated). */
  has_push_key: boolean;
  push_key_set_at: string | null;
  /** Engine stream an RTMP device pushes to; '' for other modes. */
  push_stream: string;
  /** The password itself is write-only and never returned. */
  has_password: boolean;
  enabled: boolean;
  status: DeviceStatus;
  last_seen_at: string | null;
  /** When the current status began (null until the first transition). */
  status_since: string | null;
  firmware: string;
  serial_number: string;
  last_probe_at: string | null;
  /** Failure code of the last connection test; null after a successful one. */
  last_error: ProbeErrorCode | null;
  notes: string;
  created_at: string;
  updated_at: string;
  /** Null when ONVIF answers on the web port (http_port). */
  onvif_port: number | null;
  /** Set on a DVR/NVR recorder whose channels are devices of their own (ADR-018). */
  channel_count: number | null;
  /** Set on a recorder's channel. */
  parent_id: string | null;
  channel: number | null;
  /** Read from the device itself (ADR-021); the typed vendor/model may differ. */
  detected_vendor: string;
  detected_model: string;
  mac_address: string;
  identity_source: '' | 'onvif' | 'isapi' | 'cgi';
  /** Null until the device was identified once. */
  identity_at: string | null;
  /** Channel other systems use for this camera (ADR-024); null for recorders. */
  camera_number?: number | null;
}

export const identitySourceLabels: Record<string, string> = {
  onvif: 'ONVIF',
  isapi: 'API Hikvision (ISAPI)',
  cgi: 'API Intelbras/Dahua',
};

/** A just-registered device is still being identified in the background. */
export const identifying = (d: Device, now = Date.now()) =>
  !d.identity_at &&
  d.connection !== 'rtmp' &&
  (d.parent_id ?? null) === null &&
  now - Date.parse(d.created_at) < 90_000;

// `?? null` also covers responses from before ADR-018, without the fields.
/** How the VMS reaches a device, for lists and the device card. */
export interface ConnectionInfo {
  /** Short label for a chip: "ONVIF", "RTSP direto"… */
  label: string;
  /** Ports in use, e.g. "ONVIF :80 · vídeo RTSP :554". */
  detail: string;
  /** One sentence for the device card. */
  sentence: string;
}

export function connectionInfo(d: Device): ConnectionInfo {
  const rtsp = String(d.rtsp_port);
  const onvif = String(d.onvif_port ?? d.http_port);
  if ((d.channel_count ?? null) !== null && d.connection === 'onvif')
    return {
      label: 'Gravador ONVIF',
      detail: `ONVIF :${onvif} · canais por RTSP :${rtsp}`,
      sentence: `Gravador com ${String(d.channel_count)} canais descobertos por ONVIF na porta ${onvif} (numeração e caminhos do próprio aparelho); o vídeo de cada canal vem por RTSP na porta ${rtsp}.`,
    };
  if ((d.channel_count ?? null) !== null)
    return {
      label: 'Gravador',
      detail: `canais por RTSP :${rtsp} · web :${String(d.http_port)}${d.onvif_port ? ` · ONVIF :${onvif}` : ''}`,
      sentence: `Gravador com ${String(d.channel_count)} canais: cada canal recebe o vídeo por RTSP na porta ${rtsp}; a porta web ${String(d.http_port)} serve a busca de gravações e o relógio.`,
    };
  if ((d.parent_id ?? null) !== null)
    return {
      label: 'Canal',
      detail: `RTSP :${rtsp} · do gravador`,
      sentence: `Canal ${String(d.channel)} de um gravador: vídeo por RTSP na porta ${rtsp}, com o endereço e a senha do gravador.`,
    };
  switch (d.connection) {
    case 'onvif':
      return {
        label: 'ONVIF',
        detail: `ONVIF :${onvif} · vídeo RTSP :${rtsp}`,
        sentence: `Via ONVIF na porta ${onvif} (perfis, identidade, eventos e relógio); vídeo por RTSP na porta ${rtsp}.`,
      };
    case 'rtsp':
      return {
        label: 'RTSP direto',
        detail: `RTSP :${rtsp}`,
        sentence: `Por RTSP direto na porta ${rtsp}, com os caminhos cadastrados (sem ONVIF).`,
      };
    case 'rtmp':
      return {
        label: 'RTMP reverso',
        detail: 'o aparelho envia ao servidor',
        sentence:
          'Conexão reversa RTMP: o aparelho envia o vídeo ao servidor; nenhuma porta aberta no local.',
      };
  }
}

export const isRecorder = (d: Device) => (d.channel_count ?? null) !== null;
export const isChannel = (d: Device) => (d.parent_id ?? null) !== null;

export type ProbeErrorCode =
  | 'unreachable'
  | 'timeout'
  | 'unauthorized'
  | 'not_onvif'
  | 'not_supported'
  | 'protocol_error'
  | 'blocked'
  | 'not_rtsp'
  | 'stream_not_found'
  | 'not_publishing';

export interface DeviceStream {
  profile_token: string;
  name: string;
  encoding: string;
  width: number;
  height: number;
  frame_rate: number;
  /** RTSP URI rewritten to the registered host; never contains credentials. */
  uri: string;
}

export interface ProbeReport {
  ok: boolean;
  error_code: ProbeErrorCode | null;
  latency_ms: number;
  clock_skew_seconds: number;
  manufacturer: string;
  model: string;
  firmware: string;
  serial_number: string;
  streams: DeviceStream[];
  device: Device;
}

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface SiteBody {
  client_id?: string;
  status?: Situation;
  name?: string;
  address?: string;
  timezone?: string;
}

export interface DeviceBody {
  site_id?: string;
  name?: string;
  kind?: DeviceKind;
  vendor?: string;
  model?: string;
  host?: string;
  http_port?: number;
  rtsp_port?: number;
  connection?: Connection;
  rtsp_main_path?: string;
  rtsp_sub_path?: string;
  username?: string;
  /** Omit to keep, "" to remove, any other value to replace. */
  password?: string;
  enabled?: boolean;
  notes?: string;
  /** 0 clears it (ONVIF on the web port). */
  onvif_port?: number;
  /** Creates a DVR/NVR with channels; on a recorder, adds or removes channels. */
  channel_count?: number;
}

export const sitesKey = ['sites'] as const;
export const clientsKey = ['clients'] as const;
export const devicesKey = ['devices'] as const;

function qs(params: Record<string, string | number | undefined>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') p.set(k, String(v));
  return p.toString();
}

export function listSites(
  req: AuthedRequest,
  q: { q?: string; page: number; size: number; client?: string },
) {
  return req<Page<Site>>(
    'GET',
    `/api/v1/sites?${qs({ q: q.q, client_id: q.client, limit: q.size, offset: (q.page - 1) * q.size })}`,
  );
}

export function listClients(req: AuthedRequest, q: { q?: string; page: number; size: number }) {
  return req<Page<Client>>(
    'GET',
    `/api/v1/clients?${qs({ q: q.q, limit: q.size, offset: (q.page - 1) * q.size })}`,
  );
}

const clientPath = (id: string) => `/api/v1/clients/${encodeURIComponent(id)}`;
export const getClient = (req: AuthedRequest, id: string) => req<Client>('GET', clientPath(id));
export const createClient = (req: AuthedRequest, b: ClientBody) =>
  req<Client>('POST', '/api/v1/clients', b);
export const updateClient = (req: AuthedRequest, id: string, b: ClientBody) =>
  req<Client>('PATCH', clientPath(id), b);
export const deleteClient = (req: AuthedRequest, id: string) => req<null>('DELETE', clientPath(id));

/** "123.456.789-09" / "12.345.678/0001-90" for display; other values unchanged. */
export function formatDocument(d: string | null): string {
  if (!d) return '';
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return d;
}

/** "Cliente · Conta", or just one name when the account is named after its client. */
export const accountLabel = (client: string | undefined, account: string) =>
  !client || client === account ? account : `${client} · ${account}`;

/** Accounts grouped by client, for pickers. */
export function byClient(sites: Site[]): { client: string; sites: Site[] }[] {
  const groups = new Map<string, { client: string; sites: Site[] }>();
  for (const s of sites) {
    const g = groups.get(s.client.id) ?? { client: s.client.name, sites: [] };
    g.sites.push(s);
    groups.set(s.client.id, g);
  }
  return [...groups.values()];
}

/** Every site, for pickers and filters (sites per tenant are few). */
export async function allSites(req: AuthedRequest): Promise<Site[]> {
  return (await req<Page<Site>>('GET', '/api/v1/sites?limit=100')).items;
}

export const createSite = (req: AuthedRequest, b: SiteBody) =>
  req<Site>('POST', '/api/v1/sites', b);
export const updateSite = (req: AuthedRequest, id: string, b: SiteBody) =>
  req<Site>('PATCH', `/api/v1/sites/${encodeURIComponent(id)}`, b);
export const deleteSite = (req: AuthedRequest, id: string) =>
  req<null>('DELETE', `/api/v1/sites/${encodeURIComponent(id)}`);

export interface DeviceQuery {
  q: string;
  site: string;
  kind: string;
  status: string;
  sort: string;
  page: number;
  size: number;
  /** Hides recorders' channels (they are listed inside the recorder). */
  topLevel?: boolean;
  /** Hides DVR/NVR recorders, which have no video of their own. */
  excludeRecorders?: boolean;
}

export function listDevices(req: AuthedRequest, q: DeviceQuery) {
  return req<Page<Device>>(
    'GET',
    `/api/v1/devices?${qs({
      q: q.q,
      site_id: q.site,
      kind: q.kind,
      status: q.status,
      sort: q.sort,
      top_level: q.topLevel ? 'true' : undefined,
      exclude_recorders: q.excludeRecorders ? 'true' : undefined,
      limit: q.size,
      offset: (q.page - 1) * q.size,
    })}`,
  );
}

export const createDevice = (req: AuthedRequest, b: DeviceBody) =>
  req<Device>('POST', '/api/v1/devices', b);
export const updateDevice = (req: AuthedRequest, id: string, b: DeviceBody) =>
  req<Device>('PATCH', `/api/v1/devices/${encodeURIComponent(id)}`, b);
export const getDevice = (req: AuthedRequest, id: string) =>
  req<Device>('GET', `/api/v1/devices/${encodeURIComponent(id)}`);
export const deviceKey = (id: string) => ['device', id] as const;

export const deleteDevice = (req: AuthedRequest, id: string) =>
  req<null>('DELETE', `/api/v1/devices/${encodeURIComponent(id)}`);

/** What to configure on an RTMP device; the key appears only in this answer. */
export interface PushKey {
  server_url: string;
  stream_key: string;
  url: string;
}

/** Generates a new RTMP push key; the previous one stops working at once. */
export const rotatePushKey = (req: AuthedRequest, id: string) =>
  req<PushKey>('POST', `/api/v1/devices/${encodeURIComponent(id)}/push-key`, {});

/** A device's stored login, shown to holders of devices.password (each view is audited). */
export interface DeviceCredentials {
  username: string;
  /** Empty when no password is stored. */
  password: string;
}

export const getDeviceCredentials = (req: AuthedRequest, id: string) =>
  req<DeviceCredentials>('GET', `/api/v1/devices/${encodeURIComponent(id)}/credentials`);

export const probeDevice = (req: AuthedRequest, id: string) =>
  req<ProbeReport>('POST', `/api/v1/devices/${encodeURIComponent(id)}/probe`, {});

export async function listStreams(req: AuthedRequest, id: string): Promise<DeviceStream[]> {
  return (
    await req<{ items: DeviceStream[] }>('GET', `/api/v1/devices/${encodeURIComponent(id)}/streams`)
  ).items;
}

/** One channel's result of a recorder's channel test. */
export interface ChannelProbe {
  device: Device;
  ok: boolean;
  error_code: ProbeErrorCode | null;
  stream: DeviceStream | null;
}

export interface ChannelDetection {
  ok: boolean;
  error_code: ProbeErrorCode | null;
  /** Video inputs the recorder reports over ONVIF. */
  channels: number | null;
}

const recorderPath = (id: string, rest = '') =>
  `/api/v1/devices/${encodeURIComponent(id)}/channels${rest}`;

export async function listChannels(req: AuthedRequest, id: string): Promise<Device[]> {
  return (await req<{ items: Device[] }>('GET', recorderPath(id))).items;
}
export async function probeChannels(req: AuthedRequest, id: string): Promise<ChannelProbe[]> {
  return (await req<{ items: ChannelProbe[] }>('POST', recorderPath(id, '/probe'), {})).items;
}
export const detectChannels = (req: AuthedRequest, id: string) =>
  req<ChannelDetection>('POST', recorderPath(id, '/detect'), {});

/** Channel numbers changed by asking an ONVIF recorder again (ADR-023). */
export interface Rediscovery {
  added: number[];
  updated: number[];
  missing: number[];
}
export const rediscoverChannels = (req: AuthedRequest, id: string) =>
  req<Rediscovery>('POST', recorderPath(id, '/discover'), {});
export async function setChannelsEnabled(
  req: AuthedRequest,
  id: string,
  change: { enable?: number[]; disable?: number[] },
): Promise<Device[]> {
  return (
    await req<{ items: Device[] }>('POST', recorderPath(id, '/state'), {
      enable: change.enable ?? [],
      disable: change.disable ?? [],
    })
  ).items;
}
// Under devicesKey: saving the recorder or the device list refreshes the channels.
export const channelsKey = (id: string) => [...devicesKey, 'channels', id] as const;

// Separate from devicesKey so invalidating device lists does not refetch streams.
export const streamsKey = (id: string) => ['device-streams', id] as const;

export const probeErrorLabels: Record<ProbeErrorCode, string> = {
  unreachable: 'Dispositivo inacessível: verifique endereço, porta e rede.',
  timeout: 'O dispositivo não respondeu a tempo.',
  unauthorized: 'Usuário ou senha recusados pelo dispositivo.',
  not_onvif: 'O endereço responde, mas não é um serviço ONVIF. Confira a porta HTTP/ONVIF.',
  not_supported: 'O dispositivo não oferece o serviço de mídia ONVIF necessário.',
  protocol_error: 'Resposta ONVIF inválida do dispositivo.',
  blocked:
    'Conexão bloqueada pela política de rede (o endereço resolve para uma rede não autorizada).',
  not_rtsp:
    'A porta responde, mas não é um serviço RTSP (pode ser o roteador). Confira o redirecionamento da porta RTSP.',
  not_publishing:
    'O dispositivo não está enviando vídeo. Configure nele a URL de envio RTMP gerada abaixo.',
  stream_not_found:
    'O dispositivo recusou o caminho do stream. Confira o modelo de URL e o número do canal.',
};

export const kindLabels: Record<DeviceKind, string> = {
  camera: 'Câmera',
  nvr: 'NVR',
  dvr: 'DVR',
  encoder: 'Encoder',
};

export const statusView: Record<DeviceStatus, { tone: Tone; label: string }> = {
  unknown: { tone: 'neutral', label: 'Não verificado' },
  online: { tone: 'ok', label: 'Online' },
  offline: { tone: 'crit', label: 'Offline' },
  error: { tone: 'warn', label: 'Com erro' },
};

const generic: Record<string, string> = {
  managed_by_recorder: 'Definido no gravador; altere-o lá.',
  onvif_managed: 'Vem do gravador por ONVIF; use "Redescobrir canais".',
  required: 'Campo obrigatório.',
  invalid: 'Valor inválido.',
  too_long: 'Texto longo demais.',
  out_of_range: 'Valor fora do intervalo permitido.',
};

const specific: Record<string, Record<string, string>> = {
  name: { taken: 'Já existe um registro com este nome.' },
  site_id: { required: 'Selecione um local.', unknown: 'Este local não existe mais.' },
  host: {
    invalid: 'Informe um IP ou nome de host válido, sem protocolo nem porta.',
    not_allowed:
      'Endereço bloqueado pela política de rede (loopback, link-local, metadados ou rede privada não autorizada).',
  },
  http_port: { out_of_range: 'Use uma porta entre 1 e 65535.' },
  onvif_port: { out_of_range: 'Use uma porta entre 1 e 65535, ou deixe vazio.' },
  channel_count: { out_of_range: 'Use de 1 a 64 canais.' },
  enabled: { invalid: 'O gravador fica sempre ativo; desative os canais.' },
  rtsp_port: { out_of_range: 'Use uma porta entre 1 e 65535.' },
  rtsp_main_path: {
    required: 'Informe o caminho do stream principal.',
    invalid: 'Use um caminho iniciado por /, sem espaços, usuário ou senha.',
    channel_placeholder: 'Use {channel} no lugar do número do canal.',
  },
  rtsp_sub_path: {
    invalid: 'Use um caminho iniciado por /, sem espaços, usuário ou senha.',
    channel_placeholder: 'Use {channel} no lugar do número do canal.',
  },
  timezone: { invalid: 'Fuso horário inválido.' },
  document: {
    invalid: 'CPF ou CNPJ inválido (confira os dígitos).',
    taken: 'Já existe um cliente com este CPF/CNPJ.',
  },
  client_id: {
    required: 'Selecione o cliente.',
    unknown: 'Este cliente não existe mais.',
    invalid: 'Selecione o cliente.',
  },
  status: { invalid: 'Situação inválida.' },
  connection: {
    recorder_connection:
      'O gravador mantém o modo de conexão do cadastro. Para trocar, cadastre-o de novo.',
    onvif_no_channels: 'O gravador respondeu por ONVIF, mas não listou nenhum canal com vídeo.',
  },
};

/** Translates API field error codes into operator-facing text. */
export function inventoryMessages(fields: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [f, code] of Object.entries(fields)) {
    // "onvif_<probe code>": the recorder could not be asked for its channels.
    const probe = code.startsWith('onvif_')
      ? probeErrorLabels[code.slice(6) as ProbeErrorCode]
      : undefined;
    out[f] =
      specific[f]?.[code] ??
      (probe ? `Não foi possível ler os canais por ONVIF. ${probe}` : undefined) ??
      generic[code] ??
      'Valor inválido.';
  }
  return out;
}
